import DateHelper from "./DateHelper";

export const WINDOW_MS = {
  auto: 0,
  "1m": 60 * 1000,
  "5m": 5 * 60 * 1000,
  "10m": 10 * 60 * 1000,
  "15m": 15 * 60 * 1000,
  "30m": 30 * 60 * 1000,
  "1h": 60 * 60 * 1000,
  "6h": 6 * 60 * 60 * 1000,
  "12h": 12 * 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
};
export const windowSizes = Object.keys(WINDOW_MS);
export const aggregationTypes = [
  "last",
  "raw",
  "min",
  "max",
  "sum",
  "avg",
  "rollingAvg",
  "trend",
];

export function aggregateWindows(
  data,
  windowSize,
  aggregations = ["last"],
  options = {}
) {
  data = options?.invertedDataObject
    ? data.map(([value, date]) => [date, parseFloat(value)])
    : data.map(([date, value]) => [date, parseFloat(value)]);

  if (windowSize == "auto") {
    windowSize = findSuitableWindow(
      data[0]?.[0] ?? undefined,
      data[data.length - 1]?.[0] ?? undefined
    );
  }

  if (aggregations.includes("raw") && aggregations.length === 1) {
    return data.map(([date, value]) => ({
      windowStart:
        typeof date === "string"
          ? new Date(date)
          : date instanceof Date
          ? date
          : new Date(NaN),
      windowEnd:
        typeof date === "string"
          ? new Date(date)
          : date instanceof Date
          ? date
          : new Date(NaN),
      values: [value],
      raw: value,
      first: value,
      last: value,
      min: value,
      max: value,
      sum: value,
      avg: value,
      rollingAvg: value,
      trend: 0,
    }));
  }

  const bucketSize = WINDOW_MS[windowSize];
  if (!bucketSize) throw new Error(`Invalid windowSize: ${windowSize}`);

  const buckets = {};
  let minTs = Infinity;
  let maxTs = -Infinity;

  for (const [date, value] of data) {
    const ts =
      typeof date === "string"
        ? new Date(date).getTime()
        : date instanceof Date
        ? date.getTime()
        : NaN;

    if (isNaN(ts)) {
      console.warn("Skipping invalid date:", date);
      continue;
    }

    minTs = Math.min(minTs, ts);
    maxTs = Math.max(maxTs, ts);

    const bucket = Math.floor(ts / bucketSize) * bucketSize;
    if (!buckets[bucket]) buckets[bucket] = [];
    buckets[bucket].push(value);
  }

  // Fill empty windows if requested
  if (options.fillEmpty && minTs !== Infinity && maxTs !== -Infinity) {
    let lastKnown = null;

    for (
      let t = Math.floor(minTs / bucketSize) * bucketSize;
      t <= maxTs;
      t += bucketSize
    ) {
      if (!buckets[t]) {
        if (options.fillWith === "forward") {
          if (lastKnown !== null) {
            buckets[t] = [lastKnown];
          } else {
            buckets[t] = [];
          }
        } else if (options.fillWith !== undefined) {
          buckets[t] = [options.fillWith];
        } else {
          buckets[t] = [];
        }
      } else {
        if (buckets[t].length > 0) {
          lastKnown = buckets[t][buckets[t].length - 1];
        }
      }
    }
  }

  const sortedBuckets = Object.entries(buckets)
    .map(([bucketStr, values]) => {
      const bucket = Number(bucketStr);
      const start = new Date(bucket);
      const end = new Date(start.getTime() + bucketSize);

      let stats = {
        windowStart: start,
        windowEnd: maxTs < end.getTime() ? new Date(maxTs) : end,
        values,
      };

      if (values.length > 0) {
        stats = calcAggregationStats(aggregations, values, stats);
      } else {
        stats.first = null;
        stats.last = null;
        stats.min = null;
        stats.max = null;
        stats.sum = null;
        stats.avg = null;
        stats.trend = null;
      }

      return stats;
    })
    .sort((a, b) => a.windowStart.getTime() - b.windowStart.getTime());

  // Rolling average across windows
  if (aggregations.includes("rollingAvg")) {
    const windows = [];
    let rollingSum = 0;
    let rollingCount = 0;
    let n = 5;

    const rollingWidth = WINDOW_MS[windowSize] * n;
    if (!rollingWidth)
      throw new Error(`Invalid rolling windowSize: ${windowSize}`);

    for (let i = 0; i < sortedBuckets.length; i++) {
      const bucket = sortedBuckets[i];
      const ts = bucket.windowStart.getTime();
      const sum = bucket.values.reduce((a, b) => a + b, 0);
      const count = bucket.values.length;

      // Add this bucket to the rolling window
      windows.push({ ts, sum, count });
      rollingSum += sum;
      rollingCount += count;

      // Evict buckets that fall outside the time window
      const cutoff = ts - rollingWidth;
      while (windows.length && windows[0].ts < cutoff) {
        const old = windows.shift();
        rollingSum -= old.sum;
        rollingCount -= old.count;
      }

      bucket.rollingAvg =
        rollingCount > 0
          ? +parseFloat(rollingSum / rollingCount).toFixed(precision)
          : null;
    }
  }

  return sortedBuckets;
}

function findSuitableWindow(from, to) {
  const rangeMs = new Date(to).getTime() - new Date(from).getTime();
  if (rangeMs <= 0) return "1m";

  const maxBuckets = 500;
  const entries = Object.entries(WINDOW_MS)
    .filter((v) => v[0] !== "auto") // Filter out auto
    .map(([label, sizeMs]) => ({
      label,
      sizeMs,
      buckets: rangeMs / sizeMs,
    }))
    .sort((a, b) => a.sizeMs - b.sizeMs);

  // Pick the smallest that dosent exceed maxBuckets
  for (const w of entries) {
    if (w.buckets <= maxBuckets) {
      return w.label;
    }
  }

  // Else return the largest window possible
  return entries[entries.length - 1].label;
}

const floatingPointErrorRounding = 1000000;
const precision = 3;
export function calcAggregationStats(aggregations, entries, stats = {}) {
  // Clean timestamp from data where it isn't needed
  const values = entries.map((x) => x?.value ?? x);

  stats.first =
    values.length > 0 ? +parseFloat(values[0]).toFixed(precision) : null;
  stats.last =
    values.length > 0
      ? +parseFloat(values[values.length - 1]).toFixed(precision)
      : null;
  if (aggregations.includes("min"))
    stats.min = +parseFloat(Math.min(...values)).toFixed(precision);
  if (aggregations.includes("max"))
    stats.max = +parseFloat(Math.max(...values)).toFixed(precision);
  if (aggregations.includes("sum"))
    stats.sum = +parseFloat(values.reduce((a, b) => a + b, 0)).toFixed(
      precision
    );
  if (aggregations.includes("avg"))
    stats.avg = +parseFloat(
      values.reduce((a, b) => a + b, 0) / values.length
    ).toFixed(precision);

  if (aggregations.includes("trend")) {
    const n = values.length;
    if (n > 1) {
      const x = values.map((_, i) => i);
      const y = values;
      const xMean = x.reduce((a, b) => a + b, 0) / n;
      const yMean = y.reduce((a, b) => a + b, 0) / n;
      const num = x.reduce(
        (acc, xi, i) => acc + (xi - xMean) * (y[i] - yMean),
        0
      );
      const den = x.reduce((acc, xi) => acc + (xi - xMean) ** 2, 0);
      stats.trend = den !== 0 ? +parseFloat(num / den).toFixed(precision) : 0;
    } else {
      stats.trend = 0;
    }
  }

  // Special case to calculate integral with the rest of the aggregation stats
  // Expects time marked entries
  if (aggregations.includes("integral")) {
    if (entries.length > 1) {
      let integralSum = 0;
      for (let i = 0; i < entries.length; i++) {
        if (i > 0) {
          let deltaX = DateHelper.methods.differenceInSeconds(
            entries[i]?.time,
            entries[i - 1]?.time
          );
          let midY = (entries[i]?.value + entries[i - 1]?.value) / 2;

          if (isNaN(deltaX) || isNaN(midY)) {
            console.warn("Skipping invalid Trapezoid:", deltaX, midY, i);
            continue;
          }
          //Trapezoidal_rule for integral
          integralSum += midY * deltaX;
        }
      }
      stats.integral = +parseFloat(
        Math.round(
          (integralSum + Number.EPSILON) * floatingPointErrorRounding
        ) / floatingPointErrorRounding
      ).toFixed(precision);
    } else {
      stats.integral = null;
    }
  }
  return stats;
}
